import React, { FC } from 'react';

import { Icon } from '@column/column-ui-kit';
import { cn } from '@column/ui';

import { STABLECOIN_CHAIN_LABELS } from '~/app/pages/Transfers/TypeContent/stablecoin/constants';
import { CopyRowValue } from '~/components/CopyRow';

interface Props {
  chain: string;
  className?: string;
  // Glyph size in px. ui-kit icons size via the --icon-size var, not tailwind size-* classes.
  size?: number;
}

// Network glyphs for the crypto chains. --icon-color rides currentColor to inherit text color.
// Solana isn't creatable today, but existing payment addresses may still list it, so render it.
export const ChainIcon: FC<Props> = ({ chain, className, size = 16 }) => {
  const style = { '--icon-size': `${size}px`, '--icon-color': 'currentColor' } as React.CSSProperties;
  switch (chain) {
    case 'base':
      return <Icon.Chain.Base className={className} style={style} />;
    case 'ethereum':
      return <Icon.Chain.Ethereum className={className} style={style} />;
    case 'solana':
      return <Icon.Chain.Solana className={className} style={style} />;
    default:
      return null;
  }
};

// A wrapping row of chain glyphs + labels, shared by the picker rows and the payment-address
// table cell.
export const ChainList: FC<{ chains: string[]; size?: number; className?: string; itemClassName?: string }> = ({
  chains,
  size = 14,
  className,
  itemClassName,
}) => (
  <span className={cn('flex flex-wrap items-center gap-x-3 gap-y-1 text-xs text-muted-foreground', className)}>
    {chains.map((chain) => (
      <span key={chain} className={cn('flex items-center gap-1', itemClassName)}>
        <ChainIcon chain={chain} size={size} className="shrink-0 text-muted-foreground" />
        {STABLECOIN_CHAIN_LABELS[chain] ?? chain}
      </span>
    ))}
  </span>
);

// Renders networks as a copy-row value, wrapped in CopyRowValue to align with sibling
// copy inputs.
export const NetworkValue: FC<{ chains: string[] }> = ({ chains }) => (
  <CopyRowValue>
    <div className="flex flex-col gap-2 py-2 text-sm font-medium leading-4 text-muted-foreground">
      {chains.map((chain) => (
        <span key={chain} className="flex items-center gap-1.5">
          <ChainIcon chain={chain} className="shrink-0" />
          {STABLECOIN_CHAIN_LABELS[chain] ?? chain}
        </span>
      ))}
    </div>
  </CopyRowValue>
);

// Renders a crypto asset (e.g. USDC) as a copy-row value styled like NetworkValue. --icon-size
// matches the 16px chain glyphs; color rides currentColor.
export const AssetValue: FC<{ asset: string }> = ({ asset }) => (
  <CopyRowValue>
    <div className="flex flex-col gap-2 py-2 text-sm font-medium leading-4 text-muted-foreground">
      <span className="flex items-center gap-1.5">
        <Icon.Transfer.Stablecoin
          className="shrink-0"
          style={{ '--icon-size': '16px', '--icon-color': 'currentColor' } as React.CSSProperties}
        />
        {asset}
      </span>
    </div>
  </CopyRowValue>
);
